import { useRef, useState } from 'react';
import ReactDatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../DatePicker/styles/date-picker-popper.css';

import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { FormFieldDatePickerHeader } from '@components/core-ui/form-elements/FormFieldDatePickerHeader';
import { FormFieldDatePickerHeaderMonth } from '@components/core-ui/form-elements/FormFieldDatePickerHeaderMonth';
import { FormFieldDatePickerRoot } from '@components/core-ui/form-elements/FormFieldDatePickerRoot';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import LeftIcon from '@components/icons/ArrowLeft.svg';
import RightIcon from '@components/icons/ArrowRight.svg';
import {
  datePickerPopperClassName,
  datePickerPopperModifiers,
  datePickerPopperPlacement,
} from '../DatePicker/helpers/datePickerPopperConfig';
import { useDatePickerOpenState } from '../DatePicker/helpers/useDatePickerOpenState';
import { useDatePickerTypedInputCommit } from '../DatePicker/helpers/useDatePickerTypedInputCommit';
import { Icon } from './components/Icon';
import { formatDatePickerValue } from './helpers/formatDatePickerValue';
import { monthsList } from './helpers/monthsList';
import { parseDatePickerValue } from './helpers/parseDatePickerValue';
import { TProps } from './types/TProps';

export const TextDatePicker = ({
  label,
  onChange,
  onIconClick,
  value = '',
  minDate = new Date(0),
  maxDate = new Date(),
  required = false,
  disabled = false,
  error = false,
  showTimeSelect = false,
  className = 'form-input w-full',
  dateFormat = 'MM/dd/yyyy',
}: TProps) => {
  const defaultTitle = `${label}${required ? ' *' : ''}`;
  const [title, setTitle] = useState(defaultTitle);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const { isOpen, placement, handleCalendarOpen, handleCalendarClose } = useDatePickerOpenState(wrapperRef);

  const selectedValue = parseDatePickerValue(value, dateFormat);

  const onInnerChange = (date: Date | null) => {
    if (!date) {
      onChange('');
      return;
    }
    onChange(formatDatePickerValue(date));
  };

  const commitTypedInput = useDatePickerTypedInputCommit({
    wrapperRef,
    dateFormat,
    minDate,
    maxDate,
    selected: selectedValue,
    onCommit: onInnerChange,
  });

  const onFocus = () => {
    setTitle('mm/dd/yyyy');
  };

  const onBlur = () => {
    commitTypedInput();
    setTitle(defaultTitle);
  };

  return (
    <FormFieldRoot disabled={disabled} className={className}>
      <FormFieldDatePickerRoot
        ref={wrapperRef}
        textDatePicker
        data-open={isOpen}
        data-placement={placement ?? 'bottom'}
      >
        <Icon onIconClick={onIconClick} error={error} />

        <ReactDatePicker
          dateFormat={dateFormat}
          placeholderText={title}
          showTimeSelect={showTimeSelect}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          popperClassName={datePickerPopperClassName}
          popperModifiers={datePickerPopperModifiers}
          popperPlacement={datePickerPopperPlacement}
          popperProps={{ strategy: 'fixed' }}
          onCalendarOpen={handleCalendarOpen}
          onCalendarClose={() => {
            commitTypedInput();
            handleCalendarClose();
          }}
          renderCustomHeader={({ date, decreaseMonth, increaseMonth }: ReactDatePickerCustomHeaderProps) => (
            <FormFieldDatePickerHeader>
              <IconButtonClean type="button" title="Previous month" onClick={decreaseMonth}>
                <LeftIcon />
              </IconButtonClean>
              <FormFieldDatePickerHeaderMonth>{monthsList[date.getMonth()]}</FormFieldDatePickerHeaderMonth>
              <IconButtonClean type="button" title="Next month" onClick={increaseMonth}>
                <RightIcon />
              </IconButtonClean>
            </FormFieldDatePickerHeader>
          )}
          selected={selectedValue}
          onChange={onInnerChange}
          onSelect={onInnerChange}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </FormFieldDatePickerRoot>
    </FormFieldRoot>
  );
};
